/**
 * Комментарии: выноски с текстом на чертеже, как в Ремпланнере.
 *
 * Хранятся вместе, но своего переключателя в панели слоёв у них нет: свободный комментарий
 * (стрелка на точку плана) виден всегда, а привязанный к объекту (`target`) принадлежит
 * слою этого объекта и прячется вместе с ним — это решает холст (`isCommentVisible`).
 * Новый комментарий пустой — текст пишут сразу на чертеже; оставленный пустым
 * комментарий удаляет холст, когда правку закончили.
 */
import { create } from "zustand";
import { uid, type CommentTarget, type PlanComment } from "@/shared/plan";
import type { Pt } from "@/shared/lib/geometry";

/** Кегль нового комментария, экранных пикселей. */
export const COMMENT_DEFAULT_FONT_SIZE = 13;
/** Какой кегль можно вписать в меню комментария, экранных пикселей. */
export const COMMENT_FONT_SIZE_RANGE = { min: 6, max: 72 } as const;
/** Цвета на выбор: первый — обычный, тёмный, как линии чертежа. */
export const COMMENT_COLORS = [
	"#2b3440",
	"#c62828",
	"#ef6c00",
	"#2e7d32",
	"#1565c0",
	"#6a1b9a",
] as const;

export interface CommentsLayerState {
	items: Record<string, PlanComment>;
	visible: boolean;

	/** Новый пустой комментарий: стрелка на `anchor` (или на объект `target`), рамка в `box`. */
	addComment: (anchor: Pt, box: Pt, target?: CommentTarget) => string;
	updateComment: (id: string, patch: Partial<Omit<PlanComment, "id">>) => void;
	removeComment: (id: string) => void;

	setVisible: (visible: boolean) => void;
	toggleVisible: () => void;

	restoreComments: (items: Record<string, PlanComment>) => void;
}

const round = (p: Pt): Pt => ({ x: Math.round(p.x), y: Math.round(p.y) });

export const useCommentsStore = create<CommentsLayerState>((set) => ({
	items: {},
	visible: true,

	addComment: (anchor, box, target) => {
		const id = uid("m");
		const comment: PlanComment = {
			id,
			anchor: round(anchor),
			box: round(box),
			text: "",
			fontSize: COMMENT_DEFAULT_FONT_SIZE,
			color: COMMENT_COLORS[0],
			...(target && { target }),
		};
		// Поставили комментарий — слой включаем, иначе он появится невидимым
		set((s) => ({ items: { ...s.items, [id]: comment }, visible: true }));
		return id;
	},

	updateComment: (id, patch) =>
		set((s) => {
			const comment = s.items[id];
			if (!comment) return s;
			const next = { ...comment, ...patch };
			if (patch.anchor) next.anchor = round(patch.anchor);
			if (patch.box) next.box = round(patch.box);
			return { items: { ...s.items, [id]: next } };
		}),

	removeComment: (id) =>
		set((s) => {
			if (!s.items[id]) return s;
			const items = { ...s.items };
			delete items[id];
			return { items };
		}),

	setVisible: (visible) => set({ visible }),
	toggleVisible: () => set((s) => ({ visible: !s.visible })),

	restoreComments: (items) => set({ items }),
}));
